import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, rutify } from '../apiUtils'
import { IRequest } from './RequestsServices'

export enum ChangeModule {
  Armador = 'armador',
  Tripulante = 'tripulante',
  Embarcacion = 'embarcacion',
  Proveedor = 'proveedor',
}

export enum ChangeStatus {
  PENDING = 0,
  APPLIED = 1,
}

export interface IChange {
  id: number
  modulo: ChangeModule
  id_objeto: number
  log_cambios: string
  fecha_creacion: string
  estado: ChangeStatus
}

export interface RequestedChange extends IChange {
  requestData: IRequest | undefined
}

export const defaultIChange: IChange = {
  id: 0,
  modulo: ChangeModule.Armador,
  id_objeto: 0,
  log_cambios: '',
  fecha_creacion: '',
  estado: ChangeStatus.PENDING,
}

const LOCAL_CAMBIOS_KEY = 'muelle_cambios_pendientes_storage'

function getLocalCambios(): IChange[] {
  try {
    const raw = localStorage.getItem(LOCAL_CAMBIOS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function saveLocalCambios(cambios: IChange[]) {
  try {
    localStorage.setItem(LOCAL_CAMBIOS_KEY, JSON.stringify(cambios))
  } catch (e) {
    console.error('Error saving local cambios:', e)
  }
}

export default class ChangesServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para cambios
    this.bucketID = dataStorageManager.registerBucket<IChange>(
      this.getAllCambios.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.CAMBIOS, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertCambio',
            {
              operation: this.insertCambio.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateCambio',
            {
              operation: this.updateCambio.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllCambios(): Promise<IChange[]> {
    let apiCambios: IChange[] = []
    try {
      const response = await dataQueryManager.addQuery<IChange | IChange[]>({
        query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/op_select_cambios_pendientes`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        apiCambios = response.data.filter((c) => c.log_cambios && c.log_cambios != '' && c.log_cambios.includes(':')) // Filter valid changes
      } else if (response.data) {
        if (response.data.log_cambios && response.data.log_cambios != '' && response.data.log_cambios.includes(':')) {
          apiCambios = [response.data]
        }
      }
    } catch (error) {
      console.error('Error fetching cambios:', error)
    }

    // Combinar con los cambios pendientes locales (fallbacks guardados ante error de trigger en backend)
    const localCambios = getLocalCambios()
    const merged = [...apiCambios]
    for (const lc of localCambios) {
      const existingIdx = merged.findIndex((c) => c.id === lc.id)
      if (existingIdx >= 0) {
        merged[existingIdx] = lc
      } else {
        merged.push(lc)
      }
    }
    return merged
  }

  async insertCambio(cambioData: Omit<IChange, 'id' | 'fecha_creacion' | 'estado'>): Promise<boolean> {
    try {
      if (!cambioData.log_cambios || cambioData.log_cambios == '') return true // No insertar cambios vacios, no cuenta como error
      const body = {
        qry_insert_cambios_pendientes: {
          modulo: cambioData.modulo,
          id_objeto: cambioData.id_objeto,
          log_cambios: cambioData.log_cambios,
        },
      }

      try {
        await dataQueryManager.addQuery<number>({
          query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/qry_insert_cambios_pendientes`,
          operation: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          params: [],
          body: body,
        })
      } catch (backendError) {
        console.warn(
          'Backend qry_insert_cambios_pendientes falló (posible trigger notificar_cambio_pendiente), usando fallback local:',
          backendError,
        )
      }

      // Persistir de respaldo en almacenamiento local para no bloquear la evaluación
      const localCambios = getLocalCambios()
      const newLocalId = Math.floor(Date.now() / 1000)
      const newCambio: IChange = {
        id: newLocalId,
        modulo: cambioData.modulo,
        id_objeto: Number(cambioData.id_objeto),
        log_cambios: cambioData.log_cambios,
        fecha_creacion: new Date().toISOString(),
        estado: ChangeStatus.PENDING,
      }
      localCambios.push(newCambio)
      saveLocalCambios(localCambios)

      // Inyectar en memoria en el bucket de cambios
      const current = dataStorageManager.getCurrentBucketData<IChange>(BucketDefaultIDs.CAMBIOS)
      if (current && !current.some((c) => c.id === newCambio.id)) {
        current.push(newCambio)
      }

      return true
    } catch (error) {
      console.error('Error inserting cambios:', error)
    }
    return false
  }

  async updateCambio(
    cambioData: Omit<IChange, 'modulo' | 'id_objeto' | 'log_cambios' | 'fecha_creacion' | 'estado'>,
  ): Promise<boolean> {
    try {
      try {
        const body = {
          op_update_cambios_pendientes: {
            id: cambioData.id,
          },
        }

        await dataQueryManager.addQuery<number>({
          query: `${import.meta.env.VITE_BASE_URL}/bitacora/1/op_update_cambios_pendientes`,
          operation: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          params: [],
          body,
        })
      } catch (e) {
        console.warn('op_update_cambios_pendientes backend error:', e)
      }

      // Actualizar estado en local storage
      const localCambios = getLocalCambios()
      const updatedLocal = localCambios.map((c) =>
        c.id === cambioData.id ? { ...c, estado: ChangeStatus.APPLIED } : c,
      )
      saveLocalCambios(updatedLocal)

      const current = dataStorageManager.getCurrentBucketData<IChange>(BucketDefaultIDs.CAMBIOS)
      if (current) {
        const target = current.find((c) => c.id === cambioData.id)
        if (target) target.estado = ChangeStatus.APPLIED
      }

      return true
    } catch (error) {
      console.error('Error updating cambios:', error)
    }
    return false
  }
}
